/**
 * Genesys Integration Service
 *
 * Handles Genesys Cloud Web Messaging integration for live agent handover.
 * Manages WebSocket connections, message routing, and session lifecycle.
 */

import { configService } from '../config/environment.js';
import { chatStore, MESSAGE_TYPES } from '../stores/chat.store.js';
import { generateUUID } from '../utils/storage.js';

/**
 * GenesysService class - handles Genesys Web Messaging
 *
 * Per-council config: region/deploymentId are resolved at handover time from
 * the active council's `genesys` block in VITE_COUNCIL_MAPPING (no hardcoded
 * deployment). region + deploymentId are public; any clientId/clientSecret for
 * the planned server-side token proxy stay server-side, never in this bundle.
 */
class GenesysService {
    constructor() {
        this._webSocket = null;
        this._sessionToken = null;
        this._isConnected = false;
        this._inactivityTimer = null;
        this._typingTimer = null;
        this._userDetails = null;
        this._messageCallback = null;
        this._isDisconnecting = false; // Prevent duplicate disconnect messages
        this._config = null; // Resolved per-council Genesys config for the active handover

        // Inactivity timeout (2 minutes)
        this._idleTimeoutDuration = 2 * 60 * 1000;
    }

    /**
     * Initiate handover to Genesys live agent
     * @param {Object} userDetails - User details (name, email)
     * @returns {Promise<boolean>} - Success status
     */
    async initiateHandover(userDetails = {}) {
        this._userDetails = userDetails;

        // Resolve the active council's Genesys deployment. Strict: a council
        // without a `genesys` block has no live-agent queue, so surface an
        // unavailable message instead of opening a socket to nowhere.
        this._config = configService.getGenesysConfig();
        if (!this._config) {
            const council = configService.getCouncilFromUrl();
            chatStore.addBotMessage(
                council
                    ? `Live agent support isn't available for ${council} right now. You can keep chatting with DAISY.`
                    : "Live agent support isn't available right now. You can keep chatting with DAISY.",
                MESSAGE_TYPES.GENESYS_BOT
            );
            return false;
        }

        try {
            // Connect to Web Messaging WebSocket
            await this._connectWebMessaging();

            return true;

        } catch (error) {
            console.error('Failed to initiate Genesys handover:', error);
            chatStore.addBotMessage(
                "I'm sorry, I couldn't connect you to a live agent. Please try again later.",
                MESSAGE_TYPES.GENESYS_BOT
            );
            return false;
        }
    }

    /**
     * Connect to Genesys Web Messaging WebSocket
     * @returns {Promise<void>}
     */
    async _connectWebMessaging() {
        const { region, deploymentId } = this._config;
        const websocketUrl = `wss://webmessaging.${region}/v1?deploymentId=${deploymentId}`;

        // Generate session token
        this._sessionToken = `${chatStore.conversationId}-${generateUUID()}`;

        // Activate handover in chat store
        chatStore.activateHandover();

        return new Promise((resolve, reject) => {
            this._webSocket = new WebSocket(websocketUrl);

            this._webSocket.onopen = () => {
                this._isConnected = true;
                this._sendConfigMessage();
                this._resetInactivityTimer();
                resolve();
            };

            this._webSocket.onmessage = (event) => {
                this._handleMessage(event);
            };

            this._webSocket.onerror = (error) => {
                console.error('WebSocket error:', error);
                this._cleanup();
                reject(error);
            };

            this._webSocket.onclose = () => {
                this._handleClose();
            };
        });
    }

    /**
     * Send configuration message to establish session
     */
    _sendConfigMessage() {
        const chatSummary = this._getChatSummary();

        const configMessage = {
            action: 'configureSession',
            deploymentId: this._config.deploymentId,
            token: this._sessionToken,
            memberInfo: {
                displayName: this._userDetails?.name || 'DAISY User',
                customFields: {
                    email: this._userDetails?.email || '',
                    source: 'DAISY Bot',
                    chat_summary: chatSummary
                }
            },
            startWithOffer: false
        };

        this._webSocket.send(JSON.stringify(configMessage));
    }

    /**
     * Handle incoming WebSocket message
     * @param {MessageEvent} event - WebSocket message event
     */
    _handleMessage(event) {
        this._resetInactivityTimer();

        try {
            const data = JSON.parse(event.data);

            // Handle outbound messages (from agent/bot to user)
            if (data.type === 'message' && data.body?.direction === 'Outbound') {
                this._handleOutboundMessage(data);
            }
            // Handle conversation disconnect
            else if (data.action === 'conversationDisconnected') {
                this._handleDisconnect('The agent has disconnected the chat.');
            }

        } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
        }
    }

    /**
     * Handle outbound message from agent/bot
     * @param {Object} data - Message data
     */
    _handleOutboundMessage(data) {
        const sender = data.body.originatingEntity;
        const text = data.body.text;
        const agentName = data.body.name || data.body.messenger?.name || null;

        // Add message to chat
        if (text) {
            // Clear any active typing indicator — message arrived
            clearTimeout(this._typingTimer);
            document.dispatchEvent(new CustomEvent('genesys-typing', {
                detail: { isTyping: false }
            }));

            const messageType = sender === 'Bot'
                ? MESSAGE_TYPES.GENESYS_BOT
                : MESSAGE_TYPES.GENESYS_AGENT;

            chatStore.addMessage({
                text,
                type: messageType,
                sender: 'bot',
                genesysSessionId: this._sessionToken,
                agentName
            });
        }

        // Handle events (typing, disconnect, etc.)
        if (Array.isArray(data.body.events)) {
            for (const evt of data.body.events) {
                this._handleEvent(evt);
            }
        }
    }

    /**
     * Handle Genesys events
     * @param {Object} evt - Event object
     */
    _handleEvent(evt) {
        // Handle typing indicator
        if (evt.eventType === 'Typing' && evt.typing?.type === 'On') {
            this._showTypingIndicator(evt.typing.duration || 5000);
        }

        // Handle presence disconnect
        if (evt.eventType === 'Presence' && evt.presence?.type === 'Disconnect') {
            this._handleDisconnect('The agent has left the chat.');
        }
    }

    /**
     * Show typing indicator with auto-hide
     * @param {number} duration - Duration in ms
     */
    _showTypingIndicator(duration) {
        // Dispatch event for UI to show typing
        document.dispatchEvent(new CustomEvent('genesys-typing', {
            detail: { isTyping: true }
        }));

        // Clear existing timer
        clearTimeout(this._typingTimer);

        // Auto-hide after duration
        this._typingTimer = setTimeout(() => {
            document.dispatchEvent(new CustomEvent('genesys-typing', {
                detail: { isTyping: false }
            }));
        }, duration);
    }

    /**
     * Handle disconnect from agent
     * @param {string} reason - Disconnect reason
     */
    _handleDisconnect(reason) {
        // Skip if already disconnecting (prevents duplicate messages)
        if (this._isDisconnecting) {
            return;
        }
        this._isDisconnecting = true;

        chatStore.addMessage({
            text: `${reason} You can continue chatting with DAISY.`,
            type: MESSAGE_TYPES.GENESYS_BOT,
            sender: 'bot',
            genesysSessionId: this._sessionToken
        });

        this.disconnect();
    }

    /**
     * Handle WebSocket close
     */
    _handleClose() {
        console.warn('Genesys WebSocket closed');
        this._cleanup();
    }

    /**
     * Reset inactivity timer
     */
    _resetInactivityTimer() {
        clearTimeout(this._inactivityTimer);

        this._inactivityTimer = setTimeout(() => {
            // Set flag to prevent duplicate disconnect messages
            this._isDisconnecting = true;

            chatStore.addMessage({
                text: 'Due to inactivity, the session with the live agent has been closed. You can continue chatting with DAISY.',
                type: MESSAGE_TYPES.GENESYS_BOT,
                sender: 'bot',
                genesysSessionId: this._sessionToken
            });

            this.disconnect();
        }, this._idleTimeoutDuration);
    }

    /**
     * Send message to Genesys agent
     * @param {string} text - Message text
     */
    sendMessage(text) {
        if (!this._webSocket || !this._isConnected) {
            console.error('WebSocket not connected');
            return;
        }

        const message = {
            action: 'onMessage',
            token: this._sessionToken,
            message: {
                type: 'Text',
                text
            }
        };

        this._webSocket.send(JSON.stringify(message));

        // Add user message to chat
        chatStore.addMessage({
            text,
            type: MESSAGE_TYPES.GENESYS_USER,
            sender: 'user',
            genesysSessionId: this._sessionToken
        });
    }

    /**
     * Disconnect from Genesys
     */
    disconnect() {
        if (this._webSocket) {
            this._webSocket.close();
        }
        this._cleanup();
    }

    /**
     * Cleanup resources
     */
    _cleanup() {
        clearTimeout(this._inactivityTimer);
        clearTimeout(this._typingTimer);
        this._isConnected = false;
        this._webSocket = null;
        this._isDisconnecting = false; // Reset flag for next session
        this._config = null; // Re-resolve per-council config on the next handover
        chatStore.deactivateHandover();

        // Hide typing indicator
        document.dispatchEvent(new CustomEvent('genesys-typing', {
            detail: { isTyping: false }
        }));
    }

    /**
     * Get chat summary for Genesys agent context
     * @returns {string} - Formatted chat summary
     */
    _getChatSummary() {
        const messages = chatStore.messages;
        const allowedTypes = [MESSAGE_TYPES.USER, MESSAGE_TYPES.BOT];

        // Filter relevant messages
        const filteredMessages = messages.filter(
            m => allowedTypes.includes(m.type) && m.text?.trim()
        );

        // Take last 15 messages
        const recentMessages = filteredMessages.slice(-15);

        // Format as summary
        return recentMessages.map(m => {
            const sender = m.sender === 'user' ? 'User' : 'DAISY';
            return `${sender}: ${m.text}`;
        }).join('\n');
    }

    /**
     * Check if connected to Genesys
     * @returns {boolean}
     */
    get isConnected() {
        return this._isConnected;
    }

    /**
     * Get current session token
     * @returns {string|null}
     */
    get sessionToken() {
        return this._sessionToken;
    }
}

// Export singleton instance
export const genesysService = new GenesysService();
